import { useTranslation } from "react-i18next";
import styles from "./EventBanners.module.css";
import {Button} from "@base-ui/react/button";
import {useSessionStorage} from "../hooks/useSessionStorage";
import {useEffect, useLayoutEffect, useRef, useState} from "react";
import useWindowDimensions from "../hooks/useWindowDimensions";
import {YuJaTooltip} from "./standardization/YuJaTooltip";
import {useSession} from "../hooks/useSession";
import {usePPTWebView} from "../hooks/usePPTWebView";
import {EVENT_BANNER_LOCATION_TYPES} from "../pages/CSM/EventBannerManagementPage";
import {getFormatedLocalDate} from "../utils/helpers";
import {ALL_ROLES} from "../utils/constants";
import {useLocalStorageNew} from "../hooks/useLocalStorageNew";
import {PublicApiCall} from "../apis/PublicApiCall";

export default function EventBanners() {
    const { t } = useTranslation();
    const {parseSessionToken} = useSession();
    const {listEventBanners} = PublicApiCall();
    const {isPPTPage, parseToken} = usePPTWebView();
    let [setDismissed, getDismissed] = useSessionStorage("eventBannersDismissed", []);
    const[, getHostResource] = useLocalStorageNew(window.location.hostname, {});
    const [direction, setDirection] = useState("");
    const [index, setIndex] = useState(0);
    const [eventBanners, setEventBanners] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [bannersToRender, setBannersToRender] = useState([]);
    const [showMultipleBanners, setShowMultipleBanners] = useState(eventBanners.length > 1);
    const wrapperRef = useRef();
    let {institutionId} = getHostResource();
    let payLoad = isPPTPage() ? parseToken() : parseSessionToken();
    payLoad = !!payLoad ? payLoad : {};
    const {role} = payLoad;


    useEffect(() => {
        setIsLoading(true);
        listEventBanners().then(res => {
            let today = getFormatedLocalDate(new Date());
            let dismissedIdArr = getDismissed();
            res = res.filter(banner => {
                if (banner.endDate < today) {//endDate must be greater than or equal to today
                    return false;
                }

                if (banner.location !== EVENT_BANNER_LOCATION_TYPES.ALL.value && !banner.institutions.includes(institutionId)) {
                    return false;
                }


                if (banner.location !== EVENT_BANNER_LOCATION_TYPES.ALL.value && !banner.institutions.includes(institutionId)) {
                    return false;
                }

                if (!banner.roles.includes(ALL_ROLES) && !banner.roles.includes(role)) {
                    return false;
                }

                if (dismissedIdArr.includes(banner.id)) {
                    return false;
                }

                return true;

            }).sort((a, b) => {
                // 1. Assign a Priority Score
                const getScore = (b) => {
                    if (b.endDate < today) return 3;       // Past (Lowest priority)
                    if (b.startDate > today) return 2;    // Upcoming
                    return 1;                             // Active (Highest priority)
                };

                const scoreA = getScore(a);
                const scoreB = getScore(b);

                // 2. Sort by Score first
                if (scoreA !== scoreB) {
                    return scoreA - scoreB;
                }

                // 3. Tie-breaker: Use updatedDate (Newest first)
                return new Date(b.updatedDate) - new Date(a.updatedDate);
            })

            setEventBanners(res);
        }).finally(() => setIsLoading(false));
    }, []);


    useEffect(() => {
        setShowMultipleBanners(eventBanners.length > 1);
    }, [eventBanners]);



    useEffect(() => {
        changeBannerIndex(index);

    }, [showMultipleBanners]);


    const changeBannerIndex = (idx) => {
        const nextIndex = (idx + 1) % eventBanners.length;
        const prevIndex = (idx - 1 + eventBanners.length) % eventBanners.length;

        setBannersToRender(!showMultipleBanners
            ? [{ index: idx, type: "current" }]
            : [
                { index: nextIndex, type: "next" },
                { index: idx, type: "current" },
                { index: prevIndex, type: "prev" },
            ]
        )
    }

    const onClose = () => {
        setEventBanners([]);
        setDismissed(eventBanners.map(b => b.id));
    }


    const handlePrev = () => {
        if (direction) return;
        setDirection("prev");

        setTimeout(() => {
            let idx = index === 0 ? eventBanners.length - 1 : index - 1;
            setIndex(idx);
            changeBannerIndex(idx);
            setDirection(undefined);
        }, 500);
    };

    const handleNext = () => {
        if (direction) return;
        setDirection("next");

        setTimeout(() => {
            let idx = index === eventBanners.length - 1 ? 0 : index + 1;
            setIndex(idx);
            changeBannerIndex(idx);
            setDirection(undefined);
        }, 500);
    };

    if (!!isLoading || !eventBanners || !eventBanners.length) {
        return <></>;
    }


    return (
        <div className={`${styles.EventBanner} ${!showMultipleBanners ? styles.eventBannerNoCarets : ""}`}>
            {showMultipleBanners &&
                <div className={styles.navigationButtons}>
                    <YuJaTooltip placement={"bottom"} text={t("institution.bannerPrevious")}>
                        <Button aria-label={t("institution.bannerPrevious")} onClick={handlePrev}>
                            <CaretUp/>
                        </Button>
                    </YuJaTooltip>
                    <YuJaTooltip placement={"bottom"} text={t("institution.bannerNext")}>
                        <Button aria-label={t("institution.bannerNext")} onClick={handleNext}>
                            <CaretDown/>
                        </Button>
                    </YuJaTooltip>

                </div>
            }

            <div ref={wrapperRef} className={styles.centralWrapper}>
                <div className={styles.bannerScrollContainer}>
                    {bannersToRender.map(({index, type}, idx) =>
                        <BannerContent
                            key={idx}
                            banner={eventBanners[index]}
                            type={type}
                            wrapperRef={wrapperRef}
                            direction={direction}
                            showMultipleBanners={showMultipleBanners}
                        />
                    )}
                </div>
            </div>

            <Button className={styles.closeBtn} aria-label={t("institution.bannerClose")} onClick={onClose}>
                <Close/>
            </Button>

        </div>
    );
}

export function BannerContent({banner, type, direction, wrapperRef}) {
    let {width} = useWindowDimensions();
    const [starsVisible, setStarsVisible] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const contentRef = useRef();
    const bannerClass =
        type === "current" ? styles.currentBanner : type === "prev" ? styles.prevBanner : styles.nextBanner;
    const movingClass =
        !!direction
            ? direction === "prev"
                ? `${styles.moving} ${styles.movingPrev}`
                : `${styles.moving} ${styles.movingNext}`
            : "";


    useLayoutEffect(() => {
        if (!wrapperRef.current || !contentRef.current) return;

        const calculateSpace = () => {
            if (!wrapperRef.current || !contentRef.current) return;

            const starWidthNeeded = 200;
            const wrapperWidth = wrapperRef.current?.clientWidth;
            const contentWidth = contentRef.current?.offsetWidth;
            const newVisibility = wrapperWidth - contentWidth >= starWidthNeeded;
            if (width <= 900) {
                setStarsVisible(false);
            } else {
                setStarsVisible(newVisibility);
            }
        };

        calculateSpace();
        checkIfTitleTruncated();
        const observer = new ResizeObserver(calculateSpace);
        observer.observe(wrapperRef.current);
        return () => observer.disconnect();
    }, [banner, width]);


    const checkIfTitleTruncated = () => {
        let titleEle = contentRef.current.firstElementChild;
        if (!titleEle) {
            return;
        }

        setIsTruncated(titleEle.scrollHeight > titleEle.clientHeight);
    }

    const ensureAbsoluteUrl = (url) => {
        if (!url) return '';
        return url.startsWith('http://') || url.startsWith('https://')
            ? url
            : `https://${url}`;
    };

    return (
        <div className={`${styles.bannerContent} ${bannerClass} ${movingClass} ${!starsVisible ? styles.wrapText : ""}`}>
            <PrefixImg className={styles.stars} style={{display: starsVisible ? "unset" : "none"}}/>
            <div ref={contentRef} className={styles.contentWrapper}>
                <YuJaTooltip text={banner.description} placement={"bottom"} disabled={!isTruncated} disableTab={true}>
                    <span className={styles.bannerTitle}>{banner.description}</span>
                </YuJaTooltip>

                <Button
                    className={styles.btn}
                    onClick={() => {
                        window.open(ensureAbsoluteUrl(banner.link), '_blank', 'noopener,noreferrer');
                    }}
                >
                    {banner.button}
                    <ArrowDiagonal style={{flexShrink: 0}} />
                </Button>
            </div>

            <SuffixImg className={styles.stars} style={{display: starsVisible ? "unset" : "none"}}/>


        </div>
    );
}


function CaretUp(props) {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
            <path
                d="M11.680 6.823 C 11.531 6.887,4.085 14.294,3.896 14.567 C 3.781 14.731,3.737 14.962,3.777 15.178 C 3.829 15.457,4.205 15.758,4.500 15.758 C 4.815 15.758,4.853 15.723,8.490 12.089 L 12.000 8.581 15.510 12.089 C 19.147 15.723,19.185 15.758,19.500 15.758 C 19.795 15.758,20.171 15.457,20.223 15.178 C 20.263 14.962,20.219 14.731,20.104 14.567 C 20.048 14.486,18.305 12.724,16.231 10.652 C 13.581 8.003,12.416 6.865,12.311 6.822 C 12.125 6.744,11.862 6.745,11.680 6.823 "
                stroke="none" fillRule="evenodd" fill="currentColor"></path>
        </svg>
    );
}

function CaretDown(props) {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
            <path
                d="M4.340 8.277 C 4.162 8.320,4.065 8.381,3.927 8.538 C 3.719 8.776,3.705 9.159,3.896 9.433 C 3.952 9.514,5.695 11.276,7.769 13.348 C 10.419 15.997,11.584 17.135,11.689 17.178 C 11.771 17.212,11.911 17.240,12.000 17.240 C 12.089 17.240,12.229 17.212,12.311 17.178 C 12.416 17.135,13.581 15.997,16.231 13.348 C 18.305 11.276,20.048 9.514,20.104 9.433 C 20.363 9.061,20.232 8.543,19.825 8.332 C 19.662 8.248,19.333 8.251,19.159 8.338 C 19.083 8.377,17.441 9.986,15.510 11.914 L 12.000 15.419 8.490 11.914 C 6.560 9.986,4.926 8.382,4.860 8.350 C 4.701 8.271,4.488 8.242,4.340 8.277 "
                stroke="none" fillRule="evenodd" fill="currentColor"></path>
        </svg>
    );
}

function ArrowDiagonal(props) {
    return (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
            <path
                d="M2.215 0.550 C 2.056 0.577,1.902 0.700,1.830 0.858 C 1.777 0.976,1.777 1.174,1.830 1.292 C 1.906 1.457,2.054 1.570,2.233 1.599 C 2.305 1.611,3.101 1.616,4.592 1.617 L 6.842 1.617 3.697 4.763 C 1.968 6.493,0.534 7.939,0.511 7.977 C 0.421 8.123,0.421 8.358,0.511 8.511 C 0.557 8.590,0.656 8.685,0.734 8.725 C 0.870 8.796,1.106 8.787,1.241 8.707 C 1.278 8.686,2.722 7.254,4.450 5.526 C 6.178 3.797,7.597 2.383,7.604 2.383 C 7.611 2.383,7.618 3.435,7.621 4.721 L 7.625 7.058 7.666 7.138 C 7.688 7.182,7.741 7.251,7.783 7.290 C 8.048 7.538,8.474 7.451,8.632 7.117 L 8.675 7.025 8.675 3.983 L 8.675 0.942 8.631 0.850 C 8.576 0.733,8.483 0.641,8.367 0.585 L 8.275 0.542 5.283 0.539 C 3.638 0.538,2.257 0.543,2.215 0.550 "
                stroke="none" fillRule="evenodd" fill="currentColor"></path>
        </svg>
    );
}


export function Close(props) {
    return (
        <svg width="24" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
            <path d="M17.9688 5.53125L5.03125 18.4688" stroke="currentColor" strokeWidth="1.4375"
                  strokeLinecap="round" strokeLinejoin="round"></path>
            <path d="M17.9688 18.4688L5.03125 5.53125" stroke="currentColor" strokeWidth="1.4375"
                  strokeLinecap="round" strokeLinejoin="round"></path>
        </svg>
    );
}

function PrefixImg(props) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="120" height="72" viewBox="10 15 100 55" fill="none" {...props}>
            <g filter="url(#filter0_if_21770_18003)">
                <mask id="path-1-inside-1_21770_18003" fill="white">
                    <ellipse cx="33.833" cy="33.832" rx="10.5" ry="10.5" transform="rotate(-90 33.833 33.832)"/>
                </mask>
                <ellipse cx="33.833" cy="33.832" rx="10.5" ry="10.5" transform="rotate(-90 33.833 33.832)"
                         fill="#7F56D7"/>
                <path
                    d="M33.833 23.332L33.833 34.9987C33.1887 34.9987 32.6663 34.4764 32.6663 33.832L44.333 33.832L55.9997 33.832C55.9997 21.5897 46.0753 11.6654 33.833 11.6654L33.833 23.332ZM44.333 33.832L32.6663 33.832C32.6663 33.1877 33.1887 32.6654 33.833 32.6654L33.833 44.332L33.833 55.9987C46.0753 55.9987 55.9997 46.0743 55.9997 33.832L44.333 33.832ZM33.833 44.332L33.833 32.6654C34.4773 32.6654 34.9997 33.1877 34.9997 33.832L23.333 33.832L11.6663 33.832C11.6663 46.0743 21.5907 55.9987 33.833 55.9987L33.833 44.332ZM23.333 33.832L34.9997 33.832C34.9997 34.4764 34.4773 34.9987 33.833 34.9987L33.833 23.332L33.833 11.6654C21.5907 11.6654 11.6663 21.5897 11.6663 33.832L23.333 33.832Z"
                    fill="#7F56D7" mask="url(#path-1-inside-1_21770_18003)"/>
            </g>
            <g filter="url(#filter1_if_21770_18003)">
                <circle cx="33.833" cy="33.832" r="6.5625" transform="rotate(-90 33.833 33.832)" fill="#7F56D7"
                        fillOpacity="0.7"/>
                <circle cx="33.833" cy="33.832" r="5.83333" transform="rotate(-90 33.833 33.832)" stroke="#7F56D7"
                        strokeWidth="1.45833"/>
            </g>
            <g filter="url(#filter2_if_21770_18003)">
                <circle cx="33.8343" cy="33.8307" r="2.33333" transform="rotate(-90 33.8343 33.8307)" fill="#D3BFFF"/>
            </g>
            <g filter="url(#filter3_if_21770_18003)">
                <mask id="path-6-inside-2_21770_18003" fill="white">
                    <ellipse cx="88.833" cy="37.832" rx="9.5" ry="9.5" transform="rotate(-90 88.833 37.832)"/>
                </mask>
                <ellipse cx="88.833" cy="37.832" rx="9.5" ry="9.5" transform="rotate(-90 88.833 37.832)"
                         fill="#7F56D7"/>
                <path
                    d="M88.833 28.332L88.833 38.8876C88.25 38.8876 87.7775 38.415 87.7775 37.832L98.333 37.832L108.889 37.832C108.889 26.7557 99.9094 17.7765 88.833 17.7765L88.833 28.332ZM98.333 37.832L87.7775 37.832C87.7775 37.2491 88.25 36.7765 88.833 36.7765L88.833 47.332L88.833 57.8876C99.9094 57.8876 108.889 48.9084 108.889 37.832L98.333 37.832ZM88.833 47.332L88.833 36.7765C89.416 36.7765 89.8886 37.2491 89.8886 37.832L79.333 37.832L68.7775 37.832C68.7775 48.9084 77.7566 57.8876 88.833 57.8876L88.833 47.332ZM79.333 37.832L89.8886 37.832C89.8886 38.415 89.416 38.8876 88.833 38.8876L88.833 28.332L88.833 17.7765C77.7566 17.7765 68.7775 26.7557 68.7775 37.832L79.333 37.832Z"
                    fill="#7F56D7" mask="url(#path-6-inside-2_21770_18003)"/>
            </g>
            <g filter="url(#filter4_if_21770_18003)">
                <circle cx="88.833" cy="37.832" r="5.9375" transform="rotate(-90 88.833 37.832)" fill="#7F56D7"
                        fillOpacity="0.7"/>
                <circle cx="88.833" cy="37.832" r="5.27778" transform="rotate(-90 88.833 37.832)" stroke="#7F56D7"
                        strokeWidth="1.31944"/>
            </g>
            <g filter="url(#filter5_if_21770_18003)">
                <circle cx="88.8308" cy="37.8303" r="2.11111" transform="rotate(-90 88.8308 37.8303)" fill="#D3BFFF"/>
            </g>
            <g filter="url(#filter6_if_21770_18003)">
                <circle cx="65.1455" cy="56.1445" r="6.8125" transform="rotate(-90 65.1455 56.1445)" fill="#316D83"
                        fillOpacity="0.1"/>
                <circle cx="65.1455" cy="56.1445" r="4.36" transform="rotate(-90 65.1455 56.1445)" stroke="#316D83"
                        strokeWidth="4.905"/>
            </g>
            <g filter="url(#filter7_if_21770_18003)">
                <ellipse cx="65.333" cy="56.332" rx="2" ry="2" transform="rotate(-90 65.333 56.332)" fill="white"/>
            </g>
            <defs>
                <filter id="filter0_if_21770_18003" x="-0.000328064" y="-0.00130463" width="67.6667" height="67.6667"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="4.66667"/>
                    <feGaussianBlur stdDeviation="58.3333"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18003"/>
                    <feGaussianBlur stdDeviation="11.6667" result="effect2_foregroundBlur_21770_18003"/>
                </filter>
                <filter id="filter1_if_21770_18003" x="21.4372" y="21.4362" width="24.7917" height="24.7917"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="4.66667"/>
                    <feGaussianBlur stdDeviation="58.3333"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18003"/>
                    <feGaussianBlur stdDeviation="2.91667" result="effect2_foregroundBlur_21770_18003"/>
                </filter>
                <filter id="filter2_if_21770_18003" x="30.3343" y="30.3294" width="7.00033" height="10.5013"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="4.66667"/>
                    <feGaussianBlur stdDeviation="58.3333"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18003"/>
                    <feGaussianBlur stdDeviation="0.583333" result="effect2_foregroundBlur_21770_18003"/>
                </filter>
                <filter id="filter3_if_21770_18003" x="58.2219" y="7.22092" width="61.2222" height="61.2222"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="4.22222"/>
                    <feGaussianBlur stdDeviation="52.7778"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18003"/>
                    <feGaussianBlur stdDeviation="10.5556" result="effect2_foregroundBlur_21770_18003"/>
                </filter>
                <filter id="filter4_if_21770_18003" x="77.6177" y="26.6168" width="22.4306" height="22.4306"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="4.22222"/>
                    <feGaussianBlur stdDeviation="52.7778"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18003"/>
                    <feGaussianBlur stdDeviation="2.63889" result="effect2_foregroundBlur_21770_18003"/>
                </filter>
                <filter id="filter5_if_21770_18003" x="85.6642" y="34.6632" width="6.33377" height="9.50043"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="4.22222"/>
                    <feGaussianBlur stdDeviation="52.7778"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18003"/>
                    <feGaussianBlur stdDeviation="0.527778" result="effect2_foregroundBlur_21770_18003"/>
                </filter>
                <filter id="filter6_if_21770_18003" x="49.613" y="40.612" width="31.065" height="31.065"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="3.488"/>
                    <feGaussianBlur stdDeviation="43.6"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18003"/>
                    <feGaussianBlur stdDeviation="4.36" result="effect2_foregroundBlur_21770_18003"/>
                </filter>
                <filter id="filter7_if_21770_18003" x="62.333" y="53.332" width="6" height="9"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="4"/>
                    <feGaussianBlur stdDeviation="50"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18003"/>
                    <feGaussianBlur stdDeviation="0.5" result="effect2_foregroundBlur_21770_18003"/>
                </filter>
            </defs>
        </svg>
    );
}

function SuffixImg(props) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="111" height="74" viewBox="10 15 100 55" fill="none" {...props}>
            <g filter="url(#filter0_if_21770_18025)">
                <mask id="path-1-inside-1_21770_18025" fill="white">
                    <ellipse cx="10.5" cy="10.5" rx="10.5" ry="10.5"
                             transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 86.7197 44.332)"/>
                </mask>
                <ellipse cx="10.5" cy="10.5" rx="10.5" ry="10.5"
                         transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 86.7197 44.332)" fill="#7F56D7"/>
                <path
                    d="M76.2197 23.332L76.2197 34.9987C76.8641 34.9987 77.3864 34.4764 77.3864 33.832L65.7197 33.832L54.0531 33.832C54.0531 21.5897 63.9774 11.6654 76.2197 11.6654L76.2197 23.332ZM65.7197 33.832L77.3864 33.832C77.3864 33.1877 76.8641 32.6654 76.2197 32.6654L76.2197 44.332L76.2197 55.9987C63.9774 55.9987 54.0531 46.0743 54.0531 33.832L65.7197 33.832ZM76.2197 44.332L76.2197 32.6654C75.5754 32.6654 75.0531 33.1877 75.0531 33.832L86.7197 33.832L98.3864 33.832C98.3864 46.0743 88.462 55.9987 76.2197 55.9987L76.2197 44.332ZM86.7197 33.832L75.0531 33.832C75.0531 34.4764 75.5754 34.9987 76.2197 34.9987L76.2197 23.332L76.2197 11.6654C88.462 11.6654 98.3864 21.5897 98.3864 33.832L86.7197 33.832Z"
                    fill="#7F56D7" mask="url(#path-1-inside-1_21770_18025)"/>
            </g>
            <g filter="url(#filter1_if_21770_18025)">
                <circle cx="6.5625" cy="6.5625" r="6.5625"
                        transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 82.7822 40.3945)" fill="#7F56D7"
                        fillOpacity="0.7"/>
                <circle cx="6.5625" cy="6.5625" r="5.83333"
                        transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 82.7822 40.3945)" stroke="#7F56D7"
                        strokeWidth="1.45833"/>
            </g>
            <g filter="url(#filter2_if_21770_18025)">
                <circle cx="2.33333" cy="2.33333" r="2.33333"
                        transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 78.5518 36.1641)" fill="#D3BFFF"/>
            </g>
            <g filter="url(#filter3_if_21770_18025)">
                <circle cx="6.8125" cy="6.8125" r="6.8125"
                        transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 22.3447 48.957)" fill="#316D83"
                        fillOpacity="0.1"/>
                <circle cx="6.8125" cy="6.8125" r="4.36"
                        transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 22.3447 48.957)" stroke="#316D83"
                        strokeWidth="4.905"/>
            </g>
            <g filter="url(#filter4_if_21770_18025)">
                <ellipse cx="2" cy="2" rx="2" ry="2" transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 17.3447 44.332)"
                         fill="white"/>
            </g>
            <g filter="url(#filter5_if_21770_18025)">
                <circle cx="8" cy="8" r="8" transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 50.7197 62.957)"
                        fill="#316D83" fillOpacity="0.1"/>
                <circle cx="8" cy="8" r="5.12" transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 50.7197 62.957)"
                        stroke="#316D83" strokeWidth="5.76"/>
            </g>
            <g filter="url(#filter6_if_21770_18025)">
                <circle cx="2.34862" cy="2.34862" r="2.34862"
                        transform="matrix(4.37114e-08 -1 -1 -4.37114e-08 44.8486 57.5273)" fill="white"/>
            </g>
            <defs>
                <filter id="filter0_if_21770_18025" x="42.3864" y="-0.00130463" width="67.6667" height="67.6667"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="4.66667"/>
                    <feGaussianBlur stdDeviation="58.3333"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18025"/>
                    <feGaussianBlur stdDeviation="11.6667" result="effect2_foregroundBlur_21770_18025"/>
                </filter>
                <filter id="filter1_if_21770_18025" x="63.8239" y="21.4362" width="24.7917" height="24.7917"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="4.66667"/>
                    <feGaussianBlur stdDeviation="58.3333"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18025"/>
                    <feGaussianBlur stdDeviation="2.91667" result="effect2_foregroundBlur_21770_18025"/>
                </filter>
                <filter id="filter2_if_21770_18025" x="72.7181" y="30.3294" width="7.00033" height="10.5013"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="4.66667"/>
                    <feGaussianBlur stdDeviation="58.3333"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18025"/>
                    <feGaussianBlur stdDeviation="0.583333" result="effect2_foregroundBlur_21770_18025"/>
                </filter>
                <filter id="filter3_if_21770_18025" x="-0.000272751" y="26.612" width="31.065" height="31.065"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="3.488"/>
                    <feGaussianBlur stdDeviation="43.6"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18025"/>
                    <feGaussianBlur stdDeviation="4.36" result="effect2_foregroundBlur_21770_18025"/>
                </filter>
                <filter id="filter4_if_21770_18025" x="12.3447" y="39.332" width="6" height="9"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="4"/>
                    <feGaussianBlur stdDeviation="50"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18025"/>
                    <feGaussianBlur stdDeviation="0.5" result="effect2_foregroundBlur_21770_18025"/>
                </filter>
                <filter id="filter5_if_21770_18025" x="24.4797" y="36.717" width="36.48" height="36.48"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="4.096"/>
                    <feGaussianBlur stdDeviation="51.2"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18025"/>
                    <feGaussianBlur stdDeviation="5.12" result="effect2_foregroundBlur_21770_18025"/>
                </filter>
                <filter id="filter6_if_21770_18025" x="38.9771" y="51.6577" width="7.04589" height="10.5669"
                        filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feOffset dy="4.69725"/>
                    <feGaussianBlur stdDeviation="58.7156"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.3 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_21770_18025"/>
                    <feGaussianBlur stdDeviation="0.587156" result="effect2_foregroundBlur_21770_18025"/>
                </filter>
            </defs>
        </svg>
    );
}